import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Copy, Trash2 } from "lucide-react";
import { BUSINESS_TYPE_OPTIONS, TYPE_LABELS } from "@/components/TransactionForm";
import { coaOptionsFor, lineCurrency } from "./multiEntry";

const bad = (on) => (on ? "border-[#E63946] ring-1 ring-[#E63946]/30" : "");

function Pick({ value, onChange, placeholder, testid, invalid, children }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className={`h-9 text-xs ${bad(invalid)}`}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>{children}</SelectContent>
    </Select>
  );
}

export default function EntryLineRow({
  line, index, ctx, errors, onChange, onDuplicate, onRemove, onEnterLast, canRemove, gridCols,
}) {
  const { isBusiness, categories, accounts, coa, currency } = ctx;
  const set = (patch) => onChange(line.key, patch);
  const um = line.unmatched || {};
  const ph = (f, d) => (um[f] ? `? ${um[f]}` : d);
  const tid = (n) => `ml-${n}-${index}`;
  const cur = lineCurrency(line, accounts, currency);

  const onKind = (v) => {
    const def = BUSINESS_TYPE_OPTIONS.find((o) => o.value === v);
    set({ business_kind: v, type: def?.tx_type || "expense", category: "", coa_code: "none" });
  };

  return (
    <div data-testid={`ml-row-${index}`}
         className="grid items-center gap-2 px-3 py-2 border-b border-[var(--divider)] hover:bg-[var(--card)]/60 transition-colors"
         style={{ gridTemplateColumns: gridCols }}>
      <span className="text-[11px] font-mono text-[var(--muted-text)] text-right pr-1">{index + 1}</span>
      <Input type="date" data-testid={tid("date")} value={line.date}
             onChange={(e) => set({ date: e.target.value })}
             className={`h-9 text-xs ${bad(errors.date)}`} />
      {isBusiness ? (
        <Pick value={line.business_kind} onChange={onKind} testid={tid("type")} invalid={errors.type}>
          {BUSINESS_TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </Pick>
      ) : (
        <Pick value={line.type} testid={tid("type")} invalid={errors.type}
              onChange={(v) => set({ type: v, category: "" })}>
          {Object.keys(TYPE_LABELS).map((k) => <SelectItem key={k} value={k}>{TYPE_LABELS[k]}</SelectItem>)}
        </Pick>
      )}
      <Pick value={line.category} onChange={(v) => set({ category: v })} testid={tid("category")}
            invalid={errors.category} placeholder={ph("category", isBusiness ? "Department" : "Category")}>
        {(categories[line.type] || []).map((c) => (
          <SelectItem key={c.id} value={c.name}>{c.icon} {c.name}</SelectItem>
        ))}
      </Pick>
      {isBusiness && (
        <Pick value={line.coa_code} onChange={(v) => set({ coa_code: v })} testid={tid("coa")}
              invalid={errors.coa_code} placeholder={ph("coa_code", "GL code")}>
          <SelectItem value="none">— No GL —</SelectItem>
          {coaOptionsFor(line, coa, isBusiness).map((c) => (
            <SelectItem key={c.id || c.code} value={c.code}>{c.code} · {c.name}</SelectItem>
          ))}
        </Pick>
      )}
      <Pick value={line.account_id} onChange={(v) => set({ account_id: v })} testid={tid("account")}
            invalid={errors.account_id} placeholder={ph("account_id", "Account")}>
        <SelectItem value="none">Unassigned</SelectItem>
        {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.currency}</SelectItem>)}
      </Pick>
      <Input data-testid={tid("note")} value={line.note} placeholder="Memo"
             onChange={(e) => set({ note: e.target.value })} className="h-9 text-xs" />
      <div className="relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] font-mono text-[var(--muted-text)]">{cur}</span>
        <Input data-testid={tid("amount")} type="number" step="0.01" min="0" value={line.amount}
               onChange={(e) => set({ amount: e.target.value })}
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onEnterLast(index); } }}
               className={`h-9 text-xs text-right font-mono pl-10 ${bad(errors.amount)}`} />
      </div>
      <div className="flex justify-end gap-0.5">
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" title="Duplicate line"
                data-testid={tid("dup")} onClick={() => onDuplicate(line.key)}>
          <Copy className="w-3.5 h-3.5" />
        </Button>
        <Button type="button" variant="ghost" size="icon" title="Remove line" disabled={!canRemove}
                className="h-8 w-8 hover:text-[#E63946]" data-testid={tid("remove")}
                onClick={() => onRemove(line.key)}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}
